import { Button } from "@/components/ui/button"
import ConfirmationDialog from "@/components/ui/confirmation-dialog"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { cn } from "@/lib/utils"
import { useCurrentOrganization } from "@/novus/hooks/useCurrentOrganization"
import { rootPath } from "@/novus/novus-paths"
import { useMutation } from "@apollo/client"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

const DeleteDeduction_Mutation = gql(`
  mutation DeleteDeduction($input: DeleteDeductionInput!) {
    deleteDeduction(input: $input) {
      errors {
        fullMessages
      }
    }
  }
`)

const DeleteDeductionButton = ({ id }: { id: string }) => {
  const { organization } = useCurrentOrganization()
  const [mutate, { loading }] = useMutation(DeleteDeduction_Mutation)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showPayrollLockedInfo, setShowPayrollLockedInfo] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()

  const deleteDeduction = async () => {
    try {
      mutate({
        variables: {
          input: {
            id: id,
          },
        },
      })
      toast({
        title: "Deduction has been canceled.",
      })
      navigate(rootPath.pattern)
    } catch (error) {
      console.error(error)
      toast({
        title: "Deduction was not canceled, please try again.",
        variant: "destructive",
      })
    }
  }

  const formatOrdinalDate = (day: number) => {
    const suffix = ["th", "st", "nd", "rd"]
    const v = day % 100
    return day + (suffix[(v - 20) % 10] || suffix[v] || suffix[0])
  }

  const getPayrollInfoCopy = () => {
    if (!organization?.deductionRepeatDay) return null

    const currentDate = formatOrdinalDate(organization.deductionRepeatDay)
    const nextDate = formatOrdinalDate(organization.deductionRepeatDay + 1)

    return {
      currentDate,
      nextDate,
    }
  }

  const payrollInfo = getPayrollInfoCopy()

  const handlePayrollLockedClick = () => {
    setShowPayrollLockedInfo(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handlePayrollLockedClick()
    }
  }

  return (
    <div>
      {organization?.payrollLocked ? (
        <div
          onClick={handlePayrollLockedClick}
          onKeyDown={handleKeyDown}
          className="inline-block"
          role="button"
          tabIndex={0}
        >
          <Button
            variant="link"
            disabled={true}
            className={cn("text-textPink text-sm", "hover:opacity-75")}
          >
            Cancel Deduction
          </Button>
        </div>
      ) : (
        <Button
          variant="link"
          onClick={() => setShowConfirmation(true)}
          disabled={loading}
          className={cn("text-textPink text-sm", "hover:opacity-75")}
        >
          Cancel Deduction
        </Button>
      )}
      <ConfirmationDialog
        title="Are you sure?"
        description="Please confirm that you would like to cancel this deduction."
        actionName="Cancel Deduction"
        onConfirm={() => {
          deleteDeduction()
          setShowConfirmation(false)
        }}
        isOpen={showConfirmation}
        setIsOpen={setShowConfirmation}
      />

      <Dialog
        open={showPayrollLockedInfo}
        onOpenChange={setShowPayrollLockedInfo}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deduction Enrollment Closed</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {payrollInfo && (
              <>
                <p>
                  529 deduction enrollment for this month is closed until the{" "}
                  <strong>{payrollInfo.currentDate}</strong> of the month.
                </p>
                <p>
                  You are able to opt in and manage your 529 deductions from the{" "}
                  <strong>{payrollInfo.nextDate}</strong> of the month through
                  the cutoff date of next month. Please check back during this
                  time to manage your deductions.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DeleteDeductionButton
